import type {
  BookingFinance,
  CorrectExpenseRequest,
  CurrentSession,
  ExpenseCategoryList,
  ExpenseDetail,
  PaymentMethodList,
  ReplaceConfirmedExpenseRequest,
} from '@elite-residence/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { loadExpenses } from '../api/loadOperationalLists';
import { businessDate } from '../dates.js';
import { type Dictionary, useLocale, useText } from '../i18n/LocaleProvider';
import { PageHead } from '../ui/design';
import { Icon } from '../ui/icons';
import { Alert, Field, failureOf, Loading, QueryFailure, readResponse, useAction, useFailureMessage } from '../ui/ui';
import { type ProofIntent, pendingExpenseBranch, useFinancialClient, useFinancialProofUpload } from './financialRetry';
import { loadBookingFinance, loadFinanceTask } from './loadFinanceHistory';
import { loadPendingFinance } from './loadPendingFinance';

// Finance screens (V1-6-11). Amounts stay exact strings. The server rechecks
// every permission, capacity rule and payment method.

type Finance = BookingFinance;
type Transaction = Finance['transactions'][number];
type Method = PaymentMethodList['methods'][number];
type BookingType = 'RENT_PAYMENT' | 'RENT_REFUND' | 'DEPOSIT_COLLECTION' | 'DEPOSIT_REFUND' | 'DEPOSIT_RETAINED';

const RECORD_KEYS: Record<BookingType, string> = {
  RENT_PAYMENT: 'finance.rent_payment_record',
  RENT_REFUND: 'finance.rent_refund',
  DEPOSIT_COLLECTION: 'finance.deposit_collection_record',
  DEPOSIT_REFUND: 'finance.deposit_refund',
  DEPOSIT_RETAINED: 'finance.deposit_retained',
};
const TYPES = Object.keys(RECORD_KEYS) as BookingType[];
const has = (session: CurrentSession, key: string) => session.permissions.includes(key);
const today = () => businessDate(new Date());
const proofKind = (type: string) =>
  type === 'RENT_PAYMENT'
    ? 'payment_proof'
    : type === 'DEPOSIT_COLLECTION'
      ? 'deposit_proof'
      : type === 'RENT_REFUND' || type === 'DEPOSIT_REFUND'
        ? 'refund_proof'
        : null;

const proofIds = (value: string) =>
  value
    .split(/[\s,]+/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

const text: Dictionary<
  | 'finance'
  | 'balances'
  | 'remainingRent'
  | 'netRent'
  | 'credit'
  | 'depositHeld'
  | 'record'
  | 'type'
  | 'amount'
  | 'date'
  | 'method'
  | 'reason'
  | 'transactions'
  | 'none'
  | 'withdraw'
  | 'confirm'
  | 'returnForCorrection'
  | 'reverse'
  | 'correct'
  | 'note'
  | 'expenses'
  | 'level'
  | 'company'
  | 'branch'
  | 'category'
  | 'description'
  | 'payee'
  | 'saveCategory'
  | 'code'
  | 'labelAr'
  | 'labelEn'
  | 'noCategories'
  | 'recorded'
  | 'proofs'
  | 'proofFile'
  | 'download'
  | 'otherMethod'
  | 'resubmit'
  | 'branchRequired'
  | 'supplierRefund'
  | 'recordRefund'
  | 'financeTasks'
  | 'recordTitle'
  | 'depositSeparate'
  | 'noTasks'
  | 'taskReference'
  | 'review'
  | 'replace'
  | 'invoice'
> = {
  en: {
    finance: 'Finance',
    review: 'Review',
    replace: 'Reverse and replace expense',
    invoice: 'Invoice number (optional)',
    balances: 'Balances',
    remainingRent: 'Remaining rent',
    netRent: 'Net rent collected',
    credit: 'Customer credit',
    depositHeld: 'Deposit held',
    record: 'Record',
    type: 'Type',
    amount: 'Amount (SAR)',
    date: 'Transaction date',
    method: 'Payment method',
    reason: 'Reason',
    transactions: 'Transactions',
    none: 'No transactions yet.',
    withdraw: 'Withdraw',
    confirm: 'Confirm',
    returnForCorrection: 'Return for correction',
    reverse: 'Reverse',
    correct: 'Correct',
    note: 'Note',
    expenses: 'Expenses',
    level: 'Level',
    company: 'Company',
    branch: 'Branch',
    category: 'Category',
    description: 'Description',
    payee: 'Payee',
    saveCategory: 'Save category',
    code: 'Code',
    labelAr: 'Arabic label',
    labelEn: 'English label',
    noCategories: 'No expense categories yet. An All-Branches reference administrator creates them.',
    recorded: 'Recorded',
    proofs: 'Proof version IDs',
    proofFile: 'Proof file',
    download: 'Download',
    otherMethod: 'Method description',
    resubmit: 'Resubmit',
    branchRequired: 'Choose the branch for this expense.',
    supplierRefund: 'Supplier refund',
    recordRefund: 'Record supplier refund',
    financeTasks: 'Finance tasks',
    recordTitle: 'Record a transaction',
    depositSeparate: 'The refundable deposit is kept separate from the booking total.',
    noTasks: 'No finance tasks.',
    taskReference: 'Booking reference',
  },
  ar: {
    finance: 'المالية',
    review: 'مراجعة',
    replace: 'عكس المصروف وتسجيل بديل',
    invoice: 'رقم الفاتورة (اختياري)',
    balances: 'الأرصدة',
    remainingRent: 'المتبقي من الإيجار',
    netRent: 'صافي الإيجار المحصّل',
    credit: 'رصيد للعميل',
    depositHeld: 'التأمين المحتفظ به',
    record: 'تسجيل',
    type: 'النوع',
    amount: 'المبلغ (ر.س)',
    date: 'تاريخ الحركة',
    method: 'طريقة الدفع',
    reason: 'السبب',
    transactions: 'الحركات',
    none: 'لا توجد حركات بعد.',
    withdraw: 'سحب',
    confirm: 'تأكيد',
    returnForCorrection: 'إعادة للتصحيح',
    reverse: 'عكس',
    correct: 'تصحيح',
    note: 'ملاحظة',
    expenses: 'المصروفات',
    level: 'المستوى',
    company: 'الشركة',
    branch: 'الفرع',
    category: 'التصنيف',
    description: 'الوصف',
    payee: 'المستفيد',
    saveCategory: 'حفظ التصنيف',
    code: 'الرمز',
    labelAr: 'الاسم بالعربية',
    labelEn: 'الاسم بالإنجليزية',
    noCategories: 'لا توجد تصنيفات مصروفات بعد. ينشئها مسؤول البيانات المرجعية على مستوى كل الفروع.',
    recorded: 'تم التسجيل',
    proofs: 'معرّفات نسخ الإثبات',
    proofFile: 'ملف الإثبات',
    download: 'تنزيل',
    otherMethod: 'وصف الطريقة',
    resubmit: 'إعادة الإرسال',
    branchRequired: 'اختر فرع هذا المصروف.',
    supplierRefund: 'استرداد مورد',
    recordRefund: 'تسجيل استرداد المورد',
    financeTasks: 'مهام المالية',
    recordTitle: 'تسجيل عملية',
    depositSeparate: 'التأمين المسترد منفصل عن إجمالي الحجز.',
    noTasks: 'لا توجد مهام مالية.',
    taskReference: 'مرجع الحجز',
  },
};

const TYPE_LABELS: Record<string, { en: string; ar: string }> = {
  RENT_PAYMENT: { en: 'Rent payment', ar: 'دفعة إيجار' },
  RENT_REFUND: { en: 'Rent refund', ar: 'استرداد إيجار' },
  DEPOSIT_COLLECTION: { en: 'Deposit collection', ar: 'تحصيل تأمين' },
  DEPOSIT_REFUND: { en: 'Deposit refund', ar: 'استرداد تأمين' },
  DEPOSIT_RETAINED: { en: 'Deposit retained', ar: 'تأمين محتجز' },
  EXPENSE: { en: 'Expense', ar: 'مصروف' },
  SUPPLIER_REFUND: { en: 'Supplier refund', ar: 'استرداد مورد' },
  REVERSAL: { en: 'Reversal', ar: 'عكس' },
};

const STATUS_TEXT: Record<string, { en: string; ar: string }> = {
  PENDING_VERIFICATION: { en: 'Pending verification', ar: 'بانتظار التحقق' },
  NEEDS_CORRECTION: { en: 'Needs correction', ar: 'يحتاج تصحيحاً' },
  CONFIRMED: { en: 'Confirmed', ar: 'مؤكد' },
  REVERSED: { en: 'Reversed', ar: 'معكوس' },
  WITHDRAWN: { en: 'Withdrawn', ar: 'مسحوبة' },
};

function StatusChip({ status }: { readonly status: string }) {
  const { locale } = useLocale();
  const tone =
    status === 'CONFIRMED'
      ? 'er-chip er-chip--success'
      : status === 'PENDING_VERIFICATION'
        ? 'er-chip er-chip--pending'
        : status === 'NEEDS_CORRECTION'
          ? 'er-chip er-chip--warning'
          : 'er-chip';
  return <span className={tone}>{STATUS_TEXT[status]?.[locale] ?? status}</span>;
}

function canMaintainPending(session: CurrentSession, transaction: Transaction): boolean {
  const type = transaction.type;
  if (!(type in RECORD_KEYS)) return false;
  const own = transaction.recordedBy === session.staffId && has(session, RECORD_KEYS[type as BookingType]);
  return own || has(session, 'finance.pending_manage');
}

export function BookingFinancePanel({
  bookingId,
  draft,
  session,
}: {
  readonly bookingId: string;
  readonly draft: boolean;
  readonly session: CurrentSession;
}) {
  const canView = has(session, 'finance.booking_view');
  const recordable = TYPES.filter((type) => has(session, RECORD_KEYS[type]));
  if (!canView && recordable.length === 0) return null;
  return (
    <BookingFinanceBody
      bookingId={bookingId}
      draft={draft}
      session={session}
      canView={canView}
      recordable={recordable}
    />
  );
}

function BookingFinanceBody({
  bookingId,
  draft,
  session,
  canView,
  recordable,
}: {
  readonly bookingId: string;
  readonly draft: boolean;
  readonly session: CurrentSession;
  readonly canView: boolean;
  readonly recordable: readonly BookingType[];
}) {
  const client = useFinancialClient(session.staffId);
  const locale = useLocale().locale;
  const t = useText(text);
  const message = useFailureMessage();
  const queries = useQueryClient();
  const action = useAction();
  const [acknowledgement, setAcknowledgement] = useState<string | null>(null);
  const finance = useQuery({
    queryKey: ['booking', bookingId, 'finance'],
    enabled: canView,
    queryFn: ({ signal }) => loadBookingFinance(client, bookingId, signal),
  });
  const methods = useQuery({
    queryKey: ['finance', 'methods'],
    queryFn: async ({ signal }) => {
      const response = await client.listPaymentMethods({ signal });
      if (response.status !== 200)
        return { ok: false as const, failure: failureOf(response) ?? { status: response.status } };
      return {
        ok: true as const,
        methods: response.body.methods.filter((method) => !method.disabled),
      };
    },
  });
  const refresh = () => queries.invalidateQueries({ queryKey: ['booking', bookingId, 'finance'] });
  if ((canView && finance.isPending) || methods.isPending) return <Loading />;
  const view = finance.data?.ok === true ? finance.data.body : null;
  const financeFailure = finance.isError ? { status: 500 } : finance.data?.ok === false ? finance.data.failure : null;
  const methodList = methods.data?.ok === true ? methods.data.methods : [];
  const methodFailure = methods.data?.ok === false ? methods.data.failure : null;
  const sumRow = (label: string, value: string, tone = '') => (
    <span className={`er-sum__row ${tone}`}>
      <span>
        {label}
        <span className="er-visually-hidden">: </span>
      </span>
      <b className="er-sum__v" dir="ltr">
        {value}
      </b>
    </span>
  );
  return (
    <section className="er-panel er-section-block" aria-label={t.finance}>
      <div className="er-panel__head">
        <h2 className="er-panel__title">{t.finance}</h2>
      </div>
      <div className="er-panel__body">
        {action.error && <Alert kind="error">{action.error}</Alert>}
        {financeFailure && <Alert kind="error">{message(financeFailure)}</Alert>}
        {methodFailure && <Alert kind="error">{message(methodFailure)}</Alert>}
        <div className="er-fingrid">
          <div className="er-fingrid__main">
            {view && (
              <>
                <h3 className="er-subhead">{t.transactions}</h3>
                {view.transactions.length === 0 ? (
                  <p className="er-empty">{t.none}</p>
                ) : (
                  <table className="er-ledger-table">
                    <tbody>
                      {view.transactions.map((transaction) => (
                        <tr key={transaction.id}>
                          <td>
                            <span className="er-cell-main">
                              {TYPE_LABELS[transaction.type]?.[locale] ?? transaction.type}
                            </span>
                          </td>
                          <td>
                            <StatusChip status={transaction.status} />
                          </td>
                          <td className="num er-amt" dir="ltr">
                            {transaction.revision.amount}
                          </td>
                          <td className="er-mono">{transaction.revision.date}</td>
                          <td>
                            <ProofLinks ids={transaction.revision.proofVersionIds} />
                          </td>
                          <td>
                            <TransactionActions
                              bookingId={bookingId}
                              transaction={transaction}
                              session={session}
                              methods={methodList}
                              onChanged={refresh}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
            {!draft && recordable.length > 0 && !methodFailure && (
              <div className="er-subcard">
                <h3 className="er-subhead">{t.recordTitle}</h3>
                <RecordForm
                  bookingId={bookingId}
                  staffId={session.staffId}
                  types={recordable}
                  methods={methodList}
                  busy={action.busy}
                  onSubmit={(body) =>
                    action.run(async (requestKey) => {
                      const response = await client.recordBookingFinance({
                        path: { bookingId },
                        headers: { 'idempotency-key': requestKey },
                        body,
                      });
                      if (response.status === 200 || response.status === 201) {
                        if (canView) refresh();
                        else
                          setAcknowledgement(
                            `${t.recorded} ${response.body.transaction.revision.amount} ${STATUS_TEXT[response.body.transaction.status]?.[locale] ?? response.body.transaction.status}`,
                          );
                      }
                      return response;
                    })
                  }
                />
              </div>
            )}
            {acknowledgement && <p role="status">{acknowledgement}</p>}
          </div>
          {view && (
            <aside className="er-fingrid__side">
              <h3 className="er-subhead">{t.balances}</h3>
              <p className="er-sum">
                {sumRow(t.remainingRent, view.balances.remainingRent, 'is-key')}
                {sumRow(t.netRent, view.balances.netRentCollected, 'is-ok')}
                {sumRow(t.credit, view.balances.customerCredit)}
                {sumRow(t.depositHeld, view.balances.depositHeld, 'is-dep')}
              </p>
              <p className="er-hint">{t.depositSeparate}</p>
            </aside>
          )}
        </div>
      </div>
    </section>
  );
}

function ProofLinks({ ids }: { readonly ids: readonly string[] | undefined }) {
  const t = useText(text);
  if (!ids?.length) return null;
  return (
    <>
      {ids.map((id) => (
        <a key={id} href={`/api/v1/finance/proofs/${id}/content`}>
          {t.download}
        </a>
      ))}
    </>
  );
}

function ProofUpload({
  id,
  intent,
  bookingId,
  branchId,
  kind,
  onUploaded,
}: {
  readonly id: string;
  readonly intent: ProofIntent;
  readonly bookingId?: string;
  readonly branchId?: string;
  readonly kind: string;
  readonly onUploaded: (versionId: string) => void;
}) {
  const upload = useFinancialProofUpload(intent);
  const t = useText(text);
  const action = useAction();
  return (
    <Field id={id} label={t.proofFile}>
      <input
        id={id}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (!file) return;
          const contentType = file.type === 'image/png' || file.type === 'image/jpeg' ? file.type : 'application/pdf';
          void action.run(async (requestKey) => {
            const body = new Uint8Array(await file.arrayBuffer());
            const response = await upload(
              { kind, body, contentType, ...(bookingId ? { bookingId } : {}), ...(branchId ? { branchId } : {}) },
              requestKey,
            );
            if (response.status === 200 || response.status === 201) onUploaded(response.body.versionId);
            return response;
          });
        }}
      />
      {action.error && <Alert kind="error">{action.error}</Alert>}
    </Field>
  );
}

function RecordForm({
  bookingId,
  staffId,
  types,
  methods,
  busy,
  onSubmit,
}: {
  readonly bookingId: string;
  readonly staffId: string;
  readonly types: readonly BookingType[];
  readonly methods: readonly Method[];
  readonly busy: boolean;
  readonly onSubmit: (body: {
    type: BookingType;
    amount: string;
    transactionDate: string;
    methodVersionId?: string;
    otherDescription?: string;
    reason?: string;
    proofVersionIds?: string[];
  }) => Promise<unknown>;
}) {
  const locale = useLocale().locale;
  const t = useText(text);
  const [type, setType] = useState<BookingType>(types[0] ?? 'RENT_PAYMENT');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today());
  const [methodId, setMethodId] = useState(
    methods.find((method) => method.code === 'CASH')?.id ?? methods[0]?.id ?? '',
  );
  const [reason, setReason] = useState('');
  const [otherDescription, setOtherDescription] = useState('');
  const [proofs, setProofs] = useState('');
  const retained = type === 'DEPOSIT_RETAINED';
  const method = methods.find((item) => item.id === methodId);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const ids = proofIds(proofs);
    void onSubmit({
      type,
      amount,
      transactionDate: date,
      ...(retained ? {} : { methodVersionId: methodId }),
      ...(method?.code === 'OTHER_NON_CASH' && otherDescription ? { otherDescription } : {}),
      ...(reason ? { reason } : {}),
      ...(ids.length > 0 ? { proofVersionIds: ids } : {}),
    });
  };
  return (
    <form className="er-actions" onSubmit={submit}>
      <Field id="finance-type" label={t.type}>
        <select id="finance-type" value={type} onChange={(event) => setType(event.target.value as BookingType)}>
          {types.map((item) => (
            <option key={item} value={item}>
              {TYPE_LABELS[item]?.[locale] ?? item}
            </option>
          ))}
        </select>
      </Field>
      <Field id="finance-amount" label={t.amount}>
        <input
          id="finance-amount"
          inputMode="decimal"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </Field>
      <Field id="finance-date" label={t.date}>
        <input id="finance-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
      </Field>
      {!retained && (
        <Field id="finance-method" label={t.method}>
          <select id="finance-method" value={methodId} onChange={(event) => setMethodId(event.target.value)}>
            {methods.map((method) => (
              <option key={method.id} value={method.id}>
                {locale === 'ar' ? method.labelAr : method.labelEn}
              </option>
            ))}
          </select>
        </Field>
      )}
      {method?.code === 'OTHER_NON_CASH' && (
        <Field id="finance-other" label={t.otherMethod}>
          <input
            id="finance-other"
            value={otherDescription}
            onChange={(event) => setOtherDescription(event.target.value)}
          />
        </Field>
      )}
      {method?.classification === 'non_cash' && proofKind(type) && (
        <>
          <ProofUpload
            id="finance-proof-file"
            intent={{ staffId, operation: 'recordBookingFinance', path: { bookingId } }}
            bookingId={bookingId}
            kind={proofKind(type) ?? 'payment_proof'}
            onUploaded={(id) => setProofs((current) => (current ? `${current} ${id}` : id))}
          />
          <Field id="finance-proofs" label={t.proofs}>
            <input id="finance-proofs" value={proofs} onChange={(event) => setProofs(event.target.value)} />
          </Field>
        </>
      )}
      <Field id="finance-reason" label={t.reason}>
        <input id="finance-reason" value={reason} onChange={(event) => setReason(event.target.value)} />
      </Field>
      <button className="er-button" type="submit" disabled={busy}>
        {t.record}
      </button>
    </form>
  );
}

function CorrectionForm({
  bookingId,
  staffId,
  transaction,
  methods,
  busy,
  onSubmit,
}: {
  readonly bookingId: string;
  readonly staffId: string;
  readonly transaction: Transaction;
  readonly methods: readonly Method[];
  readonly busy: boolean;
  readonly onSubmit: (body: {
    amount: string;
    transactionDate: string;
    methodVersionId?: string;
    otherDescription?: string;
    reason?: string;
    proofVersionIds?: string[];
  }) => void;
}) {
  const locale = useLocale().locale;
  const t = useText(text);
  const retained = transaction.type === 'DEPOSIT_RETAINED';
  const [amount, setAmount] = useState(transaction.revision.amount);
  const [date, setDate] = useState(transaction.revision.date);
  const [methodId, setMethodId] = useState(
    methods.some((item) => item.id === transaction.revision.methodVersionId)
      ? (transaction.revision.methodVersionId ?? '')
      : '',
  );
  const [otherDescription, setOtherDescription] = useState(transaction.revision.otherDescription ?? '');
  const [reason, setReason] = useState(transaction.revision.reason ?? '');
  const [proofs, setProofs] = useState('');
  const method = methods.find((item) => item.id === methodId);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const ids = proofIds(proofs);
    onSubmit({
      amount,
      transactionDate: date,
      ...(retained ? {} : { methodVersionId: methodId }),
      ...(method?.code === 'OTHER_NON_CASH' ? { otherDescription } : {}),
      ...(reason ? { reason } : {}),
      ...(ids.length > 0 ? { proofVersionIds: ids } : {}),
    });
  };
  return (
    <form className="er-actions" onSubmit={submit}>
      <Field id={`correct-amount-${transaction.id}`} label={t.amount}>
        <input
          id={`correct-amount-${transaction.id}`}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </Field>
      <Field id={`correct-date-${transaction.id}`} label={t.date}>
        <input
          id={`correct-date-${transaction.id}`}
          type="date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
        />
      </Field>
      {!retained && (
        <Field id={`correct-method-${transaction.id}`} label={t.method}>
          <select
            id={`correct-method-${transaction.id}`}
            value={methodId}
            onChange={(event) => setMethodId(event.target.value)}
          >
            <option value="" />
            {methods.map((item) => (
              <option key={item.id} value={item.id}>
                {locale === 'ar' ? item.labelAr : item.labelEn}
              </option>
            ))}
          </select>
        </Field>
      )}
      {method?.code === 'OTHER_NON_CASH' && (
        <Field id={`correct-other-${transaction.id}`} label={t.otherMethod}>
          <input
            id={`correct-other-${transaction.id}`}
            value={otherDescription}
            onChange={(event) => setOtherDescription(event.target.value)}
          />
        </Field>
      )}
      {method?.classification === 'non_cash' && proofKind(transaction.type) && (
        <>
          <ProofUpload
            id={`correct-proof-file-${transaction.id}`}
            intent={{
              staffId,
              operation: 'correctBookingFinance',
              path: { bookingId, transactionId: transaction.id },
            }}
            bookingId={bookingId}
            kind={proofKind(transaction.type) ?? 'payment_proof'}
            onUploaded={(id) => setProofs((current) => (current ? `${current} ${id}` : id))}
          />
          <Field id={`correct-proofs-${transaction.id}`} label={t.proofs}>
            <input
              id={`correct-proofs-${transaction.id}`}
              value={proofs}
              onChange={(event) => setProofs(event.target.value)}
            />
          </Field>
        </>
      )}
      <Field id={`correct-reason-${transaction.id}`} label={t.reason}>
        <input
          id={`correct-reason-${transaction.id}`}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>
      <button className="er-button" type="submit" disabled={busy || (!retained && !methodId)}>
        {t.resubmit}
      </button>
    </form>
  );
}

function TransactionActions({
  bookingId,
  transaction,
  session,
  methods,
  onChanged,
}: {
  readonly bookingId: string;
  readonly transaction: Transaction;
  readonly session: CurrentSession;
  readonly methods: readonly Method[];
  readonly onChanged: () => void;
}) {
  const client = useFinancialClient(session.staffId);
  const t = useText(text);
  const action = useAction();
  const [reason, setReason] = useState('');
  const [editing, setEditing] = useState(false);
  const pending = transaction.status === 'PENDING_VERIFICATION' || transaction.status === 'NEEDS_CORRECTION';
  const path = { bookingId, transactionId: transaction.id };
  const maintain = canMaintainPending(session, transaction);
  const run = (call: (key: string) => Promise<{ readonly status: number; readonly body: unknown }>) => {
    void action.run(async (requestKey) => {
      const response = await call(requestKey);
      if (response.status < 400) onChanged();
      return response;
    });
  };
  if (transaction.type === 'REVERSAL') return null;
  const showReason =
    (pending && maintain) ||
    (pending && has(session, 'finance.noncash_verify')) ||
    (transaction.status === 'CONFIRMED' && has(session, 'finance.reversal'));
  return (
    <div>
      {action.error && <Alert kind="error">{action.error}</Alert>}
      {showReason && <input aria-label={t.reason} value={reason} onChange={(event) => setReason(event.target.value)} />}
      {pending && maintain && (
        <button
          className="er-button secondary"
          type="button"
          disabled={action.busy}
          onClick={() =>
            run((key) =>
              client.withdrawBookingFinance({
                path,
                headers: { 'idempotency-key': key },
                body: { expectedRevisionId: transaction.revision.id, reason },
              }),
            )
          }
        >
          {t.withdraw}
        </button>
      )}
      {pending && has(session, 'finance.noncash_verify') && (
        <>
          <button
            className="er-button"
            type="button"
            disabled={action.busy}
            onClick={() =>
              run((key) =>
                client.verifyBookingFinance({
                  path,
                  headers: { 'idempotency-key': key },
                  body: { expectedRevisionId: transaction.revision.id, action: 'confirm' },
                }),
              )
            }
          >
            {t.confirm}
          </button>
          <button
            className="er-button secondary"
            type="button"
            disabled={action.busy}
            onClick={() =>
              run((key) =>
                client.verifyBookingFinance({
                  path,
                  headers: { 'idempotency-key': key },
                  body: { expectedRevisionId: transaction.revision.id, action: 'needs_correction', note: reason },
                }),
              )
            }
          >
            {t.returnForCorrection}
          </button>
        </>
      )}
      {pending && maintain && (
        <button className="er-button secondary" type="button" disabled={action.busy} onClick={() => setEditing(true)}>
          {t.correct}
        </button>
      )}
      {editing && (
        <CorrectionForm
          bookingId={bookingId}
          staffId={session.staffId}
          transaction={transaction}
          methods={methods}
          busy={action.busy}
          onSubmit={(body) =>
            run((key) =>
              client.correctBookingFinance({
                path,
                headers: { 'idempotency-key': key },
                body: { expectedRevisionId: transaction.revision.id, ...body },
              }),
            )
          }
        />
      )}
      {transaction.status === 'CONFIRMED' && has(session, 'finance.reversal') && (
        <button
          className="er-button secondary"
          type="button"
          disabled={action.busy}
          onClick={() =>
            run((key) =>
              client.reverseBookingFinance({
                path,
                headers: { 'idempotency-key': key },
                body: { reason },
              }),
            )
          }
        >
          {t.reverse}
        </button>
      )}
    </div>
  );
}

function ExpenseActions({
  transaction,
  session,
  methods,
  categories,
  refresh,
}: {
  readonly transaction: Transaction;
  readonly session: CurrentSession;
  readonly methods: readonly Method[];
  readonly categories: ExpenseCategoryList['categories'];
  readonly refresh: () => Promise<void>;
}) {
  const client = useFinancialClient(session.staffId);
  const t = useText(text);
  const ar = useLocale().locale === 'ar';
  const action = useAction();
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState('');
  const [editing, setEditing] = useState<'correct' | 'replace' | null>(null);
  const key = ['expense-detail', transaction.id];
  const detail = useQuery({
    queryKey: key,
    enabled: expanded,
    queryFn: async ({ signal }) => {
      const response = await client.getExpense({ path: { transactionId: transaction.id } }, { signal });
      if (response.status !== 200) throw new Error('expense_unavailable');
      return response.body;
    },
  });
  const path = { transactionId: transaction.id };
  const run = async (call: (requestKey: string) => Promise<{ readonly status: number; readonly body: unknown }>) => {
    const response = await action.run(call);
    if (response && response.status < 400) {
      setEditing(null);
      await refresh();
    }
  };
  const current = detail.data?.transaction;
  const own =
    current?.recordedBy === session.staffId &&
    has(session, current.type === 'SUPPLIER_REFUND' ? 'expense.supplier_refund' : 'expense.record');
  const pending = current?.status === 'PENDING_VERIFICATION' || current?.status === 'NEEDS_CORRECTION';
  const busy = action.busy || detail.isFetching;
  return (
    <div>
      <button className="er-button secondary" type="button" onClick={() => setExpanded(!expanded)}>
        {t.review}
      </button>
      {expanded &&
        (detail.isError ? (
          <Alert kind="error">{ar ? 'تعذر تحميل المصروف.' : 'The expense could not be loaded.'}</Alert>
        ) : !detail.data || !current ? (
          <Loading />
        ) : (
          <div>
            <p>{detail.data.detail?.description}</p>
            <p>{detail.data.detail?.payeeName}</p>
            <p dir="ltr">{detail.data.detail?.invoiceNumber}</p>
            <p>
              {t.supplierRefund}: {detail.data.account.availableSupplierRefund}
            </p>
            {action.error && <Alert kind="error">{action.error}</Alert>}
            <Field id={`expense-reason-${transaction.id}`} label={t.reason}>
              <input
                id={`expense-reason-${transaction.id}`}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </Field>
            {pending && (own || has(session, 'expense.correct')) && (
              <button
                className="er-button secondary"
                type="button"
                disabled={busy}
                onClick={() => setEditing('correct')}
              >
                {t.correct}
              </button>
            )}
            {pending && (own || has(session, 'expense.withdraw')) && (
              <button
                className="er-button secondary"
                type="button"
                disabled={busy}
                onClick={() =>
                  void run((requestKey) =>
                    client.withdrawExpense({
                      path,
                      headers: { 'idempotency-key': requestKey },
                      body: { expectedRevisionId: current.revision.id, reason },
                    }),
                  )
                }
              >
                {t.withdraw}
              </button>
            )}
            {pending && has(session, 'expense.verify') && (
              <>
                <button
                  className="er-button secondary"
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run((requestKey) =>
                      client.verifyExpense({
                        path,
                        headers: { 'idempotency-key': requestKey },
                        body: { expectedRevisionId: current.revision.id, action: 'confirm' },
                      }),
                    )
                  }
                >
                  {t.confirm}
                </button>
                <button
                  className="er-button secondary"
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run((requestKey) =>
                      client.verifyExpense({
                        path,
                        headers: { 'idempotency-key': requestKey },
                        body: { expectedRevisionId: current.revision.id, action: 'needs_correction', note: reason },
                      }),
                    )
                  }
                >
                  {t.returnForCorrection}
                </button>
              </>
            )}
            {current.status === 'CONFIRMED' && current.type !== 'REVERSAL' && has(session, 'expense.reversal') && (
              <>
                <button
                  className="er-button secondary"
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run((requestKey) =>
                      client.reverseExpense({ path, headers: { 'idempotency-key': requestKey }, body: { reason } }),
                    )
                  }
                >
                  {t.reverse}
                </button>
                {current.type === 'EXPENSE' && has(session, 'expense.record') && (
                  <button
                    className="er-button secondary"
                    type="button"
                    disabled={busy}
                    onClick={() => setEditing('replace')}
                  >
                    {t.replace}
                  </button>
                )}
              </>
            )}
            {editing && (
              <ExpenseEdit
                key={`${current.revision.id}:${editing}`}
                detail={detail.data}
                proofIntent={
                  editing === 'replace'
                    ? {
                        staffId: session.staffId,
                        operation: 'replaceConfirmedExpense',
                        path: { expenseId: transaction.id },
                      }
                    : { staffId: session.staffId, operation: 'correctExpense', path }
                }
                mode={editing}
                methods={methods}
                categories={categories}
                busy={busy}
                onSubmit={(body) =>
                  void run((requestKey) =>
                    editing === 'replace'
                      ? client.replaceConfirmedExpense({
                          path: { expenseId: transaction.id },
                          headers: { 'idempotency-key': requestKey },
                          body: body as ReplaceConfirmedExpenseRequest,
                        })
                      : client.correctExpense({
                          path,
                          headers: { 'idempotency-key': requestKey },
                          body: body as CorrectExpenseRequest,
                        }),
                  )
                }
              />
            )}
          </div>
        ))}
    </div>
  );
}

function ExpenseEdit({
  detail,
  proofIntent,
  mode,
  methods,
  categories,
  busy,
  onSubmit,
}: {
  readonly detail: ExpenseDetail;
  readonly proofIntent: ProofIntent;
  readonly mode: 'correct' | 'replace';
  readonly methods: readonly Method[];
  readonly categories: ExpenseCategoryList['categories'];
  readonly busy: boolean;
  readonly onSubmit: (body: CorrectExpenseRequest | ReplaceConfirmedExpenseRequest) => void;
}) {
  const t = useText(text);
  const ar = useLocale().locale === 'ar';
  const d = detail.detail;
  const [form, setForm] = useState({
    amount: detail.transaction.revision.amount,
    date: mode === 'replace' ? today() : detail.transaction.revision.date,
    methodId: detail.transaction.revision.methodVersionId ?? '',
    other: detail.transaction.revision.otherDescription ?? '',
    reason: '',
    proofs: mode === 'replace' ? ([] as string[]) : [...detail.transaction.revision.proofVersionIds],
    categoryId: d?.categoryId ?? '',
    description: d?.description ?? '',
    payeeName: d?.payeeName ?? '',
    invoice: d?.invoiceNumber ?? '',
    unitId: d?.unitId ?? '',
    bookingId: d?.bookingId ?? '',
    maintenanceId: d?.maintenanceId ?? '',
  });
  const set = (field: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm({ ...form, [field]: event.target.value });
  const prefix = `expense-edit-${detail.transaction.id}`;
  const method = methods.find((method) => method.id === form.methodId);
  return (
    <form
      className="er-actions"
      onSubmit={(event) => {
        event.preventDefault();
        const movement = {
          amount: form.amount,
          transactionDate: form.date,
          methodVersionId: form.methodId,
          proofVersionIds: form.proofs,
          ...(method?.code === 'OTHER_NON_CASH' ? { otherDescription: form.other } : {}),
          ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
        };
        const facts = d
          ? {
              categoryId: form.categoryId,
              description: form.description,
              payeeName: form.payeeName,
              invoiceNumber: form.invoice.trim() || null,
              unitId: form.unitId.trim() || null,
              bookingId: form.bookingId.trim() || null,
              maintenanceId: form.maintenanceId.trim() || null,
            }
          : {};
        if (mode === 'replace' && d)
          onSubmit({ ...movement, ...facts, reason: form.reason } as ReplaceConfirmedExpenseRequest);
        else if (mode === 'correct')
          onSubmit({ ...movement, ...facts, expectedRevisionId: detail.transaction.revision.id });
      }}
    >
      <Field id={`${prefix}-amount`} label={t.amount}>
        <input id={`${prefix}-amount`} value={form.amount} onChange={set('amount')} />
      </Field>
      <Field id={`${prefix}-date`} label={t.date}>
        <input id={`${prefix}-date`} type="date" value={form.date} onChange={set('date')} />
      </Field>
      <Field id={`${prefix}-method`} label={t.method}>
        <select
          id={`${prefix}-method`}
          value={form.methodId}
          onChange={(event) => setForm({ ...form, methodId: event.target.value, proofs: [] })}
        >
          <option value="" />
          {methods.map((method) => (
            <option key={method.id} value={method.id}>
              {ar ? method.labelAr : method.labelEn}
            </option>
          ))}
        </select>
      </Field>
      {method?.code === 'OTHER_NON_CASH' && (
        <Field id={`${prefix}-other`} label={t.otherMethod}>
          <input id={`${prefix}-other`} value={form.other} onChange={set('other')} />
        </Field>
      )}
      {d && (
        <>
          <Field id={`${prefix}-category`} label={t.category}>
            <select id={`${prefix}-category`} value={form.categoryId} onChange={set('categoryId')}>
              <option value="" />
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {ar ? category.labelAr : category.labelEn}
                </option>
              ))}
            </select>
          </Field>
          <Field id={`${prefix}-description`} label={t.description}>
            <input id={`${prefix}-description`} value={form.description} onChange={set('description')} />
          </Field>
          <Field id={`${prefix}-payee`} label={t.payee}>
            <input id={`${prefix}-payee`} value={form.payeeName} onChange={set('payeeName')} />
          </Field>
          <Field id={`${prefix}-invoice`} label={t.invoice}>
            <input id={`${prefix}-invoice`} value={form.invoice} onChange={set('invoice')} />
          </Field>
          {(['unitId', 'bookingId', 'maintenanceId'] as const).map((field) => (
            <Field
              key={field}
              id={`${prefix}-${field}`}
              label={
                field === 'unitId'
                  ? ar
                    ? 'معرف الوحدة (اختياري)'
                    : 'Unit id (optional)'
                  : field === 'bookingId'
                    ? ar
                      ? 'معرف الحجز (اختياري)'
                      : 'Booking id (optional)'
                    : ar
                      ? 'معرف الصيانة (اختياري)'
                      : 'Maintenance id (optional)'
              }
            >
              <input id={`${prefix}-${field}`} dir="ltr" value={form[field]} onChange={set(field)} />
            </Field>
          ))}
        </>
      )}
      {method?.classification === 'non_cash' && (
        <ProofUpload
          id={`${prefix}-file`}
          intent={proofIntent}
          kind={detail.transaction.type === 'SUPPLIER_REFUND' ? 'supplier_refund_proof' : 'expense_proof'}
          {...(detail.transaction.branchId ? { branchId: detail.transaction.branchId } : {})}
          onUploaded={(id) => setForm((current) => ({ ...current, proofs: [...current.proofs, id] }))}
        />
      )}
      <p>
        {t.proofs}: {form.proofs.length}
      </p>
      <Field id={`${prefix}-reason`} label={t.reason}>
        <input id={`${prefix}-reason`} value={form.reason} onChange={set('reason')} />
      </Field>
      <button
        className="er-button"
        type="submit"
        disabled={busy || !method || (method.classification === 'non_cash' && form.proofs.length === 0)}
      >
        {mode === 'correct' ? t.resubmit : t.replace}
      </button>
    </form>
  );
}

export function ExpensesPage({ session }: { readonly session: CurrentSession }) {
  const client = useFinancialClient(session.staffId);
  const locale = useLocale().locale;
  const t = useText(text);
  const queries = useQueryClient();
  const action = useAction();
  const canList = has(session, 'expense.view') || has(session, 'expense.record');
  const expenses = useQuery({
    queryKey: ['expenses'],
    enabled: canList,
    queryFn: ({ signal }) => loadExpenses(client, signal),
  });
  const categories = useQuery({
    queryKey: ['expense-categories'],
    queryFn: async ({ signal }) => {
      const response = await client.listExpenseCategories({ signal });
      return readResponse(response).categories;
    },
  });
  const methods = useQuery({
    queryKey: ['finance', 'payment-methods'],
    queryFn: async ({ signal }) => {
      const response = await client.listPaymentMethods({ signal });
      return readResponse(response).methods.filter((method) => !method.disabled);
    },
  });
  const branches = useQuery({
    queryKey: ['finance', 'branches', 'expense'],
    enabled: has(session, 'expense.record'),
    queryFn: async ({ signal }) => {
      const response = await client.listFinancialBranches({ signal });
      return readResponse(response).branches;
    },
  });
  // R3: only this tab's unresolved Expense may still name an archived Branch,
  // so its original request can be re-entered; the server refuses new ones.
  const recoveringBranch = pendingExpenseBranch(session.staffId, () => window.sessionStorage);
  const branchChoices = (branches.data ?? []).filter((branch) => !branch.archived || branch.id === recoveringBranch);
  const [refund, setRefund] = useState({
    expenseId: '',
    amount: '',
    transactionDate: today(),
    methodId: '',
    proofs: [] as string[],
    otherDescription: '',
    reason: '',
  });
  const [form, setForm] = useState({
    level: 'branch',
    branchId: '',
    categoryId: '',
    description: '',
    payeeName: '',
    amount: '',
    transactionDate: today(),
    code: '',
    labelAr: '',
    labelEn: '',
    proofs: '',
    methodId: '',
    otherDescription: '',
    reason: '',
    invoiceNumber: '',
  });
  const set = (field: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm({ ...form, [field]: event.target.value, ...(['level', 'branchId'].includes(field) ? { proofs: '' } : {}) });
  const refresh = async () => {
    await queries.cancelQueries({ queryKey: ['expenses'] });
    await queries.cancelQueries({ queryKey: ['expense-detail'] });
    await Promise.all([
      queries.invalidateQueries({ queryKey: ['expenses'] }),
      queries.invalidateQueries({ queryKey: ['expense-detail'] }),
      queries.invalidateQueries({ queryKey: ['expense-categories'] }),
    ]);
  };
  const expenseMessage = useFailureMessage();
  if ((canList && expenses.isPending) || categories.isPending || methods.isPending) return <Loading />;
  if (categories.isError) return <QueryFailure query={categories} />;
  if (methods.isError) return <QueryFailure query={methods} />;
  const expenseFailure = expenses.isError
    ? { status: 500 }
    : expenses.data?.ok === false
      ? expenses.data.failure
      : null;
  const expenseRows = expenses.data?.ok === true ? expenses.data.transactions : [];
  const activeMethods = (methods.data ?? []).filter((method) => !method.disabled);
  const cash = activeMethods.find((method) => method.code === 'CASH');
  const expenseMethod = form.methodId
    ? activeMethods.find((method) => method.id === form.methodId)
    : (cash ?? activeMethods[0]);
  const refundMethod = refund.methodId
    ? activeMethods.find((method) => method.id === refund.methodId)
    : (cash ?? activeMethods[0]);
  const refundExpense = expenseRows.find((row) => row.id === refund.expenseId);
  const activeCategories = (categories.data ?? []).filter((category) => !category.disabled);
  return (
    <main>
      <h1>{t.expenses}</h1>
      {action.error && <Alert kind="error">{action.error}</Alert>}
      {expenseFailure && <Alert kind="error">{expenseMessage(expenseFailure)}</Alert>}
      <QueryFailure query={branches} />
      {has(session, 'reference_data.manage') && (
        <form
          className="er-actions"
          onSubmit={(event) => {
            event.preventDefault();
            void action.run(async (requestKey) => {
              const response = await client.saveExpenseCategory({
                headers: { 'idempotency-key': requestKey },
                body: { code: form.code, labelAr: form.labelAr, labelEn: form.labelEn },
              });
              if (response.status < 400) await refresh();
              return response;
            });
          }}
        >
          <Field id="cat-code" label={t.code}>
            <input id="cat-code" value={form.code} onChange={set('code')} />
          </Field>
          <Field id="cat-ar" label={t.labelAr}>
            <input id="cat-ar" value={form.labelAr} onChange={set('labelAr')} />
          </Field>
          <Field id="cat-en" label={t.labelEn}>
            <input id="cat-en" value={form.labelEn} onChange={set('labelEn')} />
          </Field>
          <button className="er-button secondary" type="submit" disabled={action.busy}>
            {t.saveCategory}
          </button>
        </form>
      )}
      {activeCategories.length === 0 && <p>{t.noCategories}</p>}
      {has(session, 'expense.record') && activeCategories.length > 0 && expenseMethod && (
        <form
          className="er-actions"
          onSubmit={(event) => {
            event.preventDefault();
            if (form.level === 'branch' && !form.branchId) {
              action.setError(t.branchRequired);
              return;
            }
            void action.run(async (requestKey) => {
              const response = await client.recordExpense({
                headers: { 'idempotency-key': requestKey },
                body: {
                  level: form.level === 'company' ? 'company' : 'branch',
                  ...(form.level === 'branch' ? { branchId: form.branchId } : {}),
                  categoryId: form.categoryId || (activeCategories[0]?.id ?? ''),
                  description: form.description,
                  payeeName: form.payeeName,
                  amount: form.amount,
                  transactionDate: form.transactionDate,
                  methodVersionId: expenseMethod.id,
                  ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
                  ...(form.invoiceNumber.trim() ? { invoiceNumber: form.invoiceNumber.trim() } : {}),
                  ...(expenseMethod.code === 'OTHER_NON_CASH' ? { otherDescription: form.otherDescription } : {}),
                  ...(proofIds(form.proofs).length > 0 ? { proofVersionIds: proofIds(form.proofs) } : {}),
                },
              });
              if (response.status < 400) await refresh();
              return response;
            });
          }}
        >
          <Field id="exp-level" label={t.level}>
            <select id="exp-level" value={form.level} onChange={set('level')}>
              <option value="branch">{t.branch}</option>
              <option value="company">{t.company}</option>
            </select>
          </Field>
          {form.level === 'branch' && (
            <Field id="exp-branch" label={t.branch}>
              <select id="exp-branch" value={form.branchId} onChange={set('branchId')}>
                <option value="" />
                {branchChoices.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.archived ? `${branch.name} (${locale === 'ar' ? 'مؤرشف' : 'archived'})` : branch.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field id="exp-category" label={t.category}>
            <select id="exp-category" value={form.categoryId} onChange={set('categoryId')}>
              {activeCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {locale === 'ar' ? category.labelAr : category.labelEn}
                </option>
              ))}
            </select>
          </Field>
          <Field id="exp-description" label={t.description}>
            <input id="exp-description" value={form.description} onChange={set('description')} />
          </Field>
          <Field id="exp-payee" label={t.payee}>
            <input id="exp-payee" value={form.payeeName} onChange={set('payeeName')} />
          </Field>
          <Field id="exp-amount" label={t.amount}>
            <input id="exp-amount" inputMode="decimal" value={form.amount} onChange={set('amount')} />
          </Field>
          <Field id="exp-date" label={t.date}>
            <input id="exp-date" type="date" value={form.transactionDate} onChange={set('transactionDate')} />
          </Field>
          <Field id="exp-method" label={t.method}>
            <select
              id="exp-method"
              value={expenseMethod.id}
              onChange={(event) => setForm({ ...form, methodId: event.target.value, proofs: '' })}
            >
              {activeMethods.map((method) => (
                <option key={method.id} value={method.id}>
                  {locale === 'ar' ? method.labelAr : method.labelEn}
                </option>
              ))}
            </select>
          </Field>
          {expenseMethod.code === 'OTHER_NON_CASH' && (
            <Field id="exp-other" label={t.otherMethod}>
              <input id="exp-other" value={form.otherDescription} onChange={set('otherDescription')} />
            </Field>
          )}
          <Field id="exp-reason" label={t.reason}>
            <input id="exp-reason" value={form.reason} onChange={set('reason')} />
          </Field>
          <Field id="exp-invoice" label={t.invoice}>
            <input id="exp-invoice" value={form.invoiceNumber} onChange={set('invoiceNumber')} />
          </Field>
          <ProofUpload
            id="exp-proof-file"
            intent={{ staffId: session.staffId, operation: 'recordExpense' }}
            kind="expense_proof"
            key={`${form.level}:${form.branchId}`}
            {...(form.level === 'branch' && form.branchId ? { branchId: form.branchId } : {})}
            onUploaded={(id) =>
              setForm((current) => ({ ...current, proofs: current.proofs ? `${current.proofs} ${id}` : id }))
            }
          />
          <p>
            {t.proofs}: {proofIds(form.proofs).length}
          </p>
          <button
            className="er-button"
            type="submit"
            disabled={
              action.busy ||
              (form.level === 'branch' && !form.branchId) ||
              (expenseMethod.classification === 'non_cash' && proofIds(form.proofs).length === 0)
            }
          >
            {t.record}
          </button>
        </form>
      )}
      {has(session, 'expense.supplier_refund') && refundMethod && (
        <form
          className="er-actions"
          onSubmit={(event) => {
            event.preventDefault();
            if (!refund.expenseId) return;
            void action.run(async (requestKey) => {
              const response = await client.recordSupplierRefund({
                path: { expenseId: refund.expenseId },
                headers: { 'idempotency-key': requestKey },
                body: {
                  amount: refund.amount,
                  transactionDate: refund.transactionDate,
                  methodVersionId: refundMethod.id,
                  ...(refund.proofs.length ? { proofVersionIds: refund.proofs } : {}),
                  ...(refundMethod.code === 'OTHER_NON_CASH' ? { otherDescription: refund.otherDescription } : {}),
                  ...(refund.reason ? { reason: refund.reason } : {}),
                },
              });
              if (response.status < 400) await refresh();
              return response;
            });
          }}
        >
          <Field id="refund-expense" label={t.supplierRefund}>
            <select
              id="refund-expense"
              value={refund.expenseId}
              onChange={(event) => setRefund({ ...refund, expenseId: event.target.value, proofs: [] })}
            >
              <option value="" />
              {expenseRows
                .filter((row) => row.type === 'EXPENSE' && row.status === 'CONFIRMED')
                .map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.revision.amount} {row.revision.date}
                  </option>
                ))}
            </select>
          </Field>
          <Field id="refund-method" label={t.method}>
            <select
              id="refund-method"
              value={refundMethod.id}
              onChange={(event) => setRefund({ ...refund, methodId: event.target.value })}
            >
              {activeMethods.map((method) => (
                <option key={method.id} value={method.id}>
                  {locale === 'ar' ? method.labelAr : method.labelEn}
                </option>
              ))}
            </select>
          </Field>
          {refundMethod.code === 'OTHER_NON_CASH' && (
            <Field id="refund-other" label={t.otherMethod}>
              <input
                id="refund-other"
                value={refund.otherDescription}
                onChange={(event) => setRefund({ ...refund, otherDescription: event.target.value })}
              />
            </Field>
          )}
          {refundMethod.classification === 'non_cash' && refundExpense && (
            <>
              <ProofUpload
                key={refund.expenseId}
                id="refund-proof-file"
                intent={{
                  staffId: session.staffId,
                  operation: 'recordSupplierRefund',
                  path: { expenseId: refund.expenseId },
                }}
                kind="supplier_refund_proof"
                {...(refundExpense.branchId ? { branchId: refundExpense.branchId } : {})}
                onUploaded={(id) => setRefund((current) => ({ ...current, proofs: [...current.proofs, id] }))}
              />
              <p>
                {locale === 'ar' ? 'الإثباتات المرفوعة' : 'Uploaded proofs'}: {refund.proofs.length}
              </p>
            </>
          )}
          <Field id="refund-reason" label={t.reason}>
            <input
              id="refund-reason"
              value={refund.reason}
              onChange={(event) => setRefund({ ...refund, reason: event.target.value })}
            />
          </Field>
          <Field id="refund-amount" label={t.amount}>
            <input
              id="refund-amount"
              inputMode="decimal"
              value={refund.amount}
              onChange={(event) => setRefund({ ...refund, amount: event.target.value })}
            />
          </Field>
          <Field id="refund-date" label={t.date}>
            <input
              id="refund-date"
              type="date"
              value={refund.transactionDate}
              onChange={(event) => setRefund({ ...refund, transactionDate: event.target.value })}
            />
          </Field>
          <button
            className="er-button"
            type="submit"
            disabled={
              action.busy ||
              !refund.expenseId ||
              !refund.amount ||
              (refundMethod.classification === 'non_cash' && refund.proofs.length === 0) ||
              (refundMethod.code === 'OTHER_NON_CASH' && !refund.otherDescription)
            }
          >
            {t.recordRefund}
          </button>
        </form>
      )}
      <table className="er-table">
        <tbody>
          {expenseRows.map((transaction) => (
            <tr key={transaction.id}>
              <td>{TYPE_LABELS[transaction.type]?.[locale] ?? transaction.type}</td>
              <td>
                <StatusChip status={transaction.status} />
              </td>
              <td dir="ltr">{transaction.revision.amount}</td>
              <td className="er-mono">{transaction.revision.date}</td>
              <td>{has(session, 'expense.view') && <ProofLinks ids={transaction.revision.proofVersionIds} />}</td>
              <td>
                <ExpenseActions
                  transaction={transaction}
                  session={session}
                  methods={activeMethods}
                  categories={activeCategories}
                  refresh={refresh}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}

const TASK_KEYS = [
  'finance.booking_view',
  'finance.pending_manage',
  'finance.noncash_verify',
  'finance.rent_payment_record',
  'finance.rent_refund',
  'finance.deposit_collection_record',
  'finance.deposit_refund',
  'finance.deposit_retained',
];

const OUTGOING = new Set(['RENT_REFUND', 'DEPOSIT_REFUND', 'SUPPLIER_REFUND']);

const financeText: Dictionary<
  | 'subtitle'
  | 'period'
  | 'last30'
  | 'last90'
  | 'branch'
  | 'verification'
  | 'expensesTab'
  | 'reportsTab'
  | 'queue'
  | 'needsCorrection'
  | 'allVerified'
  | 'select'
  | 'selectHint'
  | 'rentReceived'
  | 'rentReturned'
  | 'netRent'
  | 'pendingRent'
  | 'remaining'
  | 'remainingSub'
  | 'depositHeld'
  | 'depositSub'
  | 'expenses'
  | 'toVerify'
  | 'notCounted'
  | 'confirmedOnly'
  | 'negative'
  | 'noData'
  | 'moneyIn'
  | 'moneyOut'
  | 'type'
  | 'status'
  | 'date'
  | 'method'
  | 'proof'
  | 'noProof'
  | 'ledger'
  | 'bookingBalance'
  | 'queueFailed'
> = {
  en: {
    subtitle: 'Every amount is its own transaction; nothing is overwritten.',
    period: 'Period',
    last30: 'Last 30 days',
    last90: 'Last 90 days',
    branch: 'Branch',
    verification: 'Bank verification',
    expensesTab: 'Expenses',
    reportsTab: 'Reports',
    queue: 'Pending bank verification',
    needsCorrection: 'Returned for correction',
    allVerified: 'All bank transactions are verified.',
    select: 'Select a transaction',
    selectHint: 'Choose a transaction in the queue to review its proof and the booking balance.',
    rentReceived: 'Rent received',
    rentReturned: 'Rent refunds',
    netRent: 'Net rent',
    pendingRent: 'Pending rent',
    remaining: 'Remaining balance',
    remainingSub: 'Open bookings with a balance',
    depositHeld: 'Deposits held',
    depositSub: 'Refundable, not income',
    expenses: 'Branch expenses',
    toVerify: 'To verify',
    notCounted: 'Not counted until confirmed',
    confirmedOnly: 'Confirmed only',
    negative: 'Separate negative entries',
    noData: 'No Data',
    moneyIn: 'money in',
    moneyOut: 'money out',
    type: 'Type',
    status: 'Status',
    date: 'Transaction date',
    method: 'Method',
    proof: 'Proof',
    noProof: 'No proof attached.',
    ledger: 'Booking transactions',
    bookingBalance: 'Booking balance',
    queueFailed: 'The queue could not be loaded.',
  },
  ar: {
    subtitle: 'كل مبلغ عملية مستقلة، ولا يُستبدل أي قيد.',
    period: 'الفترة',
    last30: 'آخر 30 يوماً',
    last90: 'آخر 90 يوماً',
    branch: 'الفرع',
    verification: 'التحقق البنكي',
    expensesTab: 'المصروفات',
    reportsTab: 'التقارير',
    queue: 'بانتظار التحقق البنكي',
    needsCorrection: 'أُعيدت للتصحيح',
    allVerified: 'تم التحقق من كل العمليات البنكية.',
    select: 'اختر عملية',
    selectHint: 'اختر عملية من القائمة لمراجعة الإثبات ورصيد الحجز.',
    rentReceived: 'الإيجار المقبوض',
    rentReturned: 'استردادات الإيجار',
    netRent: 'صافي الإيجار',
    pendingRent: 'إيجار معلّق',
    remaining: 'الرصيد المتبقي',
    remainingSub: 'حجوزات قائمة عليها رصيد',
    depositHeld: 'التأمينات المحتجزة',
    depositSub: 'مستردة، ليست إيرادًا',
    expenses: 'مصروفات الفرع',
    toVerify: 'للتحقق',
    notCounted: 'لا تُحتسب حتى التأكيد',
    confirmedOnly: 'المؤكد فقط',
    negative: 'قيود سالبة مستقلة',
    noData: 'لا توجد بيانات',
    moneyIn: 'وارد',
    moneyOut: 'صادر',
    type: 'النوع',
    status: 'الحالة',
    date: 'تاريخ العملية',
    method: 'طريقة الدفع',
    proof: 'الإثبات',
    noProof: 'لا إثبات مرفق.',
    ledger: 'عمليات الحجز',
    bookingBalance: 'رصيد الحجز',
    queueFailed: 'تعذر تحميل القائمة.',
  },
};

function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

// Summary cards from the real finance reports (finance.reports only).
function FinanceSummary({
  session,
  pendingCount,
}: {
  readonly session: CurrentSession;
  readonly pendingCount: number | null;
}) {
  const client = useApiClient();
  const t = useText(financeText);
  const [branch, setBranch] = useState('');
  const [days, setDays] = useState(30);
  const allowed = has(session, 'finance.reports');
  const branches = useQuery({
    queryKey: ['finance', 'summary', 'branches'],
    enabled: allowed,
    queryFn: async ({ signal }) => readResponse(await client.listFinancialBranches({ signal })).branches,
  });
  const branchId = branches.data?.find((b) => b.id === branch)?.id ?? branches.data?.[0]?.id;
  const to = addDays(today(), 1);
  const from = addDays(today(), 1 - days);
  const range = branchId ? { branchId, from, to } : null;
  const rent = useQuery({
    queryKey: ['finance', 'summary', 'rent', range],
    enabled: Boolean(range),
    queryFn: async ({ signal }) =>
      range ? readResponse(await client.rentMovement({ query: range }, { signal })) : null,
  });
  const deposits = useQuery({
    queryKey: ['finance', 'summary', 'deposits', range],
    enabled: Boolean(range),
    queryFn: async ({ signal }) =>
      range ? readResponse(await client.depositMovement({ query: range }, { signal })) : null,
  });
  const expenses = useQuery({
    queryKey: ['finance', 'summary', 'expenses', range],
    enabled: Boolean(range) && has(session, 'expense.report'),
    queryFn: async ({ signal }) =>
      range ? readResponse(await client.expenseMovement({ query: range }, { signal })) : null,
  });
  const balances = useQuery({
    queryKey: ['finance', 'summary', 'balances', branchId],
    enabled: Boolean(branchId),
    queryFn: async ({ signal }) =>
      branchId ? readResponse(await client.bookingBalanceSummary({ query: { branchId } }, { signal })) : null,
  });
  if (!allowed) return null;
  const value = (query: { isPending: boolean; isError: boolean }, v: string | null | undefined) =>
    query.isPending ? '…' : query.isError || v == null ? t.noData : `SAR ${v}`;
  const card = (label: string, v: string, sub: string, cls = '') => (
    <div className={`er-stat ${cls}`}>
      <span className="er-stat__label">{label}</span>
      <span className="er-stat__value" dir="ltr">
        {v}
      </span>
      <span className="er-stat__sub">{sub}</span>
    </div>
  );
  return (
    <>
      <div className="er-tools">
        <select
          aria-label={t.branch}
          value={branchId ?? ''}
          style={{ width: 240 }}
          onChange={(e) => setBranch(e.target.value)}
        >
          {(branches.data ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <select
          aria-label={t.period}
          value={days}
          style={{ width: 180 }}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={30}>{t.last30}</option>
          <option value={90}>{t.last90}</option>
        </select>
      </div>
      <div className="er-money">
        {card(t.rentReceived, value(rent, rent.data?.rentReceived), t.confirmedOnly)}
        {card(t.rentReturned, value(rent, rent.data?.rentReturned), t.negative, 'er-stat--out')}
        {card(t.netRent, value(rent, rent.data?.netRent), `${t.rentReceived} − ${t.rentReturned}`)}
        {card(t.pendingRent, value(rent, rent.data?.pendingRent), t.notCounted)}
        {card(
          t.remaining,
          value(balances, balances.data?.positiveBalances),
          balances.data ? `${balances.data.positiveBookingCount} · ${t.remainingSub}` : t.remainingSub,
        )}
        {card(t.depositHeld, value(deposits, deposits.data?.depositHeld), t.depositSub)}
        {has(session, 'expense.report')
          ? card(t.expenses, value(expenses, expenses.data?.branchNet), t.confirmedOnly)
          : null}
        {pendingCount !== null ? card(t.toVerify, String(pendingCount), t.notCounted) : null}
      </div>
    </>
  );
}

function FinanceWorkspace({
  session,
  selected,
  children,
}: {
  readonly session: CurrentSession;
  readonly selected: string | null;
  readonly children: ReactNode;
}) {
  const client = useApiClient();
  const locale = useLocale().locale;
  const t = useText(text);
  const f = useText(financeText);
  const tasks = useQuery({
    queryKey: ['finance', 'tasks'],
    queryFn: async ({ signal }) => loadPendingFinance(client, signal),
  });
  const items = tasks.data ?? [];
  const queue = items.filter((item) => item.status === 'PENDING_VERIFICATION');
  const returned = items.filter((item) => item.status !== 'PENDING_VERIFICATION');
  const row = (item: (typeof items)[number]) => (
    <Link
      key={item.transactionId}
      className={`er-fq${item.transactionId === selected ? ' is-active' : ''}`}
      to={`/finance/tasks/${item.transactionId}`}
      aria-current={item.transactionId === selected ? 'page' : undefined}
    >
      <span style={{ minWidth: 0, flex: 1 }}>
        <span className="er-cell-main er-mono" style={{ display: 'block' }}>
          {item.displayReference}
        </span>
        <span className="er-cell-sub" style={{ display: 'block' }}>
          {TYPE_LABELS[item.type]?.[locale] ?? item.type}
        </span>
      </span>
      <span className="er-fq__amt">
        <b dir="ltr">{item.amount}</b>
        <span className="er-cell-sub">{OUTGOING.has(item.type) ? f.moneyOut : f.moneyIn}</span>
      </span>
    </Link>
  );
  return (
    <main>
      <PageHead title={t.finance} sub={f.subtitle} />
      <FinanceSummary session={session} pendingCount={tasks.data ? queue.length : null} />
      <nav className="er-tabs" aria-label={t.finance}>
        <Link className="er-tab" to="/finance" aria-current="page">
          {f.verification}
          {tasks.data ? <span className="er-tab__count">{queue.length}</span> : null}
        </Link>
        {['expense.view', 'expense.record', 'expense.supplier_refund'].some((k) => has(session, k)) ? (
          <Link className="er-tab" to="/expenses">
            {f.expensesTab}
          </Link>
        ) : null}
        {has(session, 'finance.reports') ? (
          <Link className="er-tab" to="/reports">
            {f.reportsTab}
          </Link>
        ) : null}
      </nav>
      <div className="er-fsplit">
        <section className="er-panel er-flist" aria-label={t.financeTasks}>
          <h2 className="er-visually-hidden">{t.financeTasks}</h2>
          <div className="er-flist__h">
            <span className="er-overline">{f.queue}</span>
            <span className="er-cell-sub">{tasks.data ? queue.length : ''}</span>
          </div>
          {tasks.isPending ? (
            <Loading />
          ) : tasks.isError ? (
            <div className="er-panel__body">
              <QueryFailure query={tasks} />
            </div>
          ) : items.length === 0 ? (
            <div className="er-empty">{t.noTasks}</div>
          ) : (
            <>
              {queue.length ? queue.map(row) : <div className="er-empty">{f.allVerified}</div>}
              {returned.length ? (
                <>
                  <div className="er-flist__h">
                    <span className="er-overline">{f.needsCorrection}</span>
                    <span className="er-cell-sub">{returned.length}</span>
                  </div>
                  {returned.map(row)}
                </>
              ) : null}
            </>
          )}
        </section>
        <div className="er-fdetail-wrap">{children}</div>
      </div>
    </main>
  );
}

export function FinanceTasksPage({ session }: { readonly session: CurrentSession }) {
  const f = useText(financeText);
  if (!TASK_KEYS.some((key) => has(session, key))) return null;
  return (
    <FinanceWorkspace session={session} selected={null}>
      <section className="er-panel">
        <div className="er-empty" style={{ padding: '56px 16px' }}>
          <div className="er-panel__title" style={{ marginBottom: 6 }}>
            {f.select}
          </div>
          {f.selectHint}
        </div>
      </section>
    </FinanceWorkspace>
  );
}

export function FinanceTaskPage({ session }: { readonly session: CurrentSession }) {
  const { transactionId = '' } = useParams();
  const client = useApiClient();
  const locale = useLocale().locale;
  const t = useText(text);
  const f = useText(financeText);
  const message = useFailureMessage();
  const queries = useQueryClient();
  const task = useQuery({
    queryKey: ['finance', 'task', transactionId],
    queryFn: ({ signal }) => loadFinanceTask(client, transactionId, signal),
  });
  const methods = useQuery({
    queryKey: ['finance', 'methods'],
    queryFn: async ({ signal }) => {
      const response = await client.listPaymentMethods({ signal });
      if (response.status !== 200)
        return { ok: false as const, failure: failureOf(response) ?? { status: response.status } };
      return { ok: true as const, methods: response.body.methods.filter((method) => !method.disabled) };
    },
  });
  const failure = task.isError ? { status: 500 } : task.data?.ok === false ? task.data.failure : null;
  const methodFailure = methods.data?.ok === false ? methods.data.failure : null;
  const body = task.data?.ok === true ? task.data.body : null;
  const methodList = methods.data?.ok === true ? methods.data.methods : [];
  const refresh = () => queries.invalidateQueries({ queryKey: ['finance', 'task', transactionId] });
  const focus = body?.transactions.find((tx) => tx.id === transactionId) ?? body?.transactions[0];
  const methodLabel = (id: string | null | undefined) => {
    const m = methodList.find((x) => x.id === id);
    return m ? (locale === 'ar' ? m.labelAr : m.labelEn) : '—';
  };
  return (
    <FinanceWorkspace session={session} selected={transactionId}>
      {task.isPending || methods.isPending ? (
        <Loading />
      ) : (
        <>
          {failure && <Alert kind="error">{message(failure)}</Alert>}
          {methodFailure && <Alert kind="error">{message(methodFailure)}</Alert>}
          {body && focus && (
            <section className="er-panel er-fdetail" aria-label={t.finance}>
              <div className="er-fdetail__head">
                <div>
                  <div className="er-overline">
                    {`${TYPE_LABELS[focus.type]?.[locale] ?? focus.type} · ${OUTGOING.has(focus.type) ? f.moneyOut : f.moneyIn}`}
                  </div>
                  <div className="er-fdetail__amt" dir="ltr">{`SAR ${focus.revision.amount}`}</div>
                </div>
                <span className="er-spacer" />
                <StatusChip status={focus.status} />
              </div>
              <div className="er-fdetail__body">
                <div>
                  <dl className="er-kv">
                    <dt>{t.taskReference}</dt>
                    <dd>
                      {has(session, 'booking.view') ? (
                        <Link to={`/bookings/${body.bookingId}`}>{body.displayReference}</Link>
                      ) : (
                        <span>{body.displayReference}</span>
                      )}
                    </dd>
                    <dt>{f.type}</dt>
                    <dd>{TYPE_LABELS[focus.type]?.[locale] ?? focus.type}</dd>
                    <dt>{f.method}</dt>
                    <dd>{methodLabel(focus.revision.methodVersionId)}</dd>
                    <dt>{f.date}</dt>
                    <dd className="er-mono">{`${focus.revision.date}`}</dd>
                  </dl>
                  <div className="er-fctx">
                    <span className="er-overline">{t.balances}</span>
                    <dl className="er-kv">
                      <dt>{t.remainingRent}</dt>
                      <dd dir="ltr">{body.balances.remainingRent}</dd>
                      <dt>{t.netRent}</dt>
                      <dd dir="ltr">{body.balances.netRentCollected}</dd>
                      <dt>{t.credit}</dt>
                      <dd dir="ltr">{body.balances.customerCredit}</dd>
                      <dt>{t.depositHeld}</dt>
                      <dd dir="ltr">{body.balances.depositHeld}</dd>
                    </dl>
                  </div>
                </div>
                <div>
                  <span className="er-overline">{f.proof}</span>
                  {focus.revision.proofVersionIds?.length ? (
                    <ProofLinks ids={focus.revision.proofVersionIds} />
                  ) : (
                    <p className="er-cell-sub">{f.noProof}</p>
                  )}
                </div>
              </div>
              <div className="er-panel__head" style={{ borderTop: '1px solid var(--line)' }}>
                <h2 className="er-panel__title">{t.transactions}</h2>
              </div>
              <div className="er-ledger">
                {body.transactions.map((transaction) => (
                  <div key={transaction.id} className={`er-entry${transaction.id === focus.id ? ' is-focus' : ''}`}>
                    <span
                      className={`er-event__ico ${OUTGOING.has(transaction.type) ? 'er-event__ico--bad' : 'er-event__ico--ok'}`}
                    >
                      <Icon name={OUTGOING.has(transaction.type) ? 'undo' : 'receipt'} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div className="er-entry__top">
                        <span className="er-cell-main">
                          {TYPE_LABELS[transaction.type]?.[locale] ?? transaction.type}
                        </span>
                        <StatusChip status={transaction.status} />
                      </div>
                      <div className="er-cell-sub er-mono">{transaction.revision.date}</div>
                      <TransactionActions
                        bookingId={body.bookingId}
                        transaction={transaction}
                        session={session}
                        methods={methodList}
                        onChanged={refresh}
                      />
                    </div>
                    <span className="er-amt" dir="ltr">
                      {transaction.id === focus.id ? `${transaction.revision.amount}` : transaction.revision.amount}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </FinanceWorkspace>
  );
}
